/**
 * Recipe Instructions Parser
 *
 * Parses recipe instructions from server format to structured steps
 * Server format: "Step 1: instruction text Step 2: instruction text..."
 * Output: Array of step objects with step number and instruction text
 */

export interface RecipeStep {
  stepNumber: number;
  instruction: string;
}

/**
 * Parse recipe instructions from server string format
 *
 * @param instructionsText - Raw instructions text from server
 * @returns Array of parsed recipe steps
 *
 * Primary format: "1. Title\nDescription\n2. Title\nDescription..."
 * Fallback format: "Grill pork\nHeat oven" (treated as single step)
 *
 * Example: "1. Grill pork\nHeat oven\n2. Boil rice\nCook 15 min"
 * Returns: [
 *   { stepNumber: 1, instruction: "Grill pork\nHeat oven" },
 *   { stepNumber: 2, instruction: "Boil rice\nCook 15 min" }
 * ]
 *
 * Fallback example: "Grill a pork tenderloin\nHeat oven to 400°F"
 * Returns: [
 *   { stepNumber: 1, instruction: "Grill a pork tenderloin\nHeat oven to 400°F" }
 * ]
 */
export const parseRecipeInstructions = (
  instructionsText: string
): RecipeStep[] => {
  if (!instructionsText?.trim()) {
    return [];
  }

  try {
    const steps: RecipeStep[] = [];

    // Split by numbered items (1. 2. 3. etc.) keeping the numbers
    const parts = instructionsText.split(/(?=\d+\.\s)/);

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) {
        continue;
      }

      // Extract step number and content: "1. Some content here"
      const match = trimmed.match(/^(\d+)\.\s*([\s\S]*?)$/);

      if (match) {
        const stepNumber = parseInt(match[1], 10);
        const instruction = match[2].trim();

        if (!isNaN(stepNumber) && instruction) {
          steps.push({ stepNumber, instruction });
        }
      }
    }

    // If no numbered steps found, check for fallback scenario
    if (steps.length === 0) {
      const trimmedText = instructionsText.trim();

      // If there's actual content (not empty), treat it as a single step
      // This handles cases where nutritionists forgot to add numbers
      if (trimmedText.length > 0) {
        steps.push({
          stepNumber: 1,
          instruction: trimmedText,
        });
      }
    }

    return steps.sort((a, b) => a.stepNumber - b.stepNumber);
  } catch (error) {
    return [];
  }
};

/**
 * Example usage:
 *
 * // Numbered format (primary server format):
 * const numberedInstructions = `1. Grill a pork tenderloin fillet
 * Heat oven to 400°F | 200°C. Grill a pork tenderloin fillet in the oven for about 40 minutes
 * 2. Boil the rice
 * While the pork tenderloin is being cooked, start cooking the rice. Boil the rice for about 15-20 minutes.
 * 3. Cook the Brussels sprouts
 * Meanwhile, boil or steam the Brussels sprouts for about 8-15 minutes.`;
 *
 * const steps = parseRecipeInstructions(numberedInstructions);
 * // Returns:
 * // [
 * //   { stepNumber: 1, instruction: "Grill a pork tenderloin fillet\nHeat oven to 400°F | 200°C. Grill a pork tenderloin fillet in the oven for about 40 minutes" },
 * //   { stepNumber: 2, instruction: "Boil the rice\nWhile the pork tenderloin is being cooked, start cooking the rice. Boil the rice for about 15-20 minutes." },
 * //   { stepNumber: 3, instruction: "Cook the Brussels sprouts\nMeanwhile, boil or steam the Brussels sprouts for about 8-15 minutes." }
 * // ]
 *
 * // Fallback format (single step without numbers):
 * const fallbackInstructions = `Grill a pork tenderloin fillet
 * Heat oven to 400°F | 200°C. Grill a pork tenderloin fillet in the oven for about 40 minutes`;
 *
 * const fallbackSteps = parseRecipeInstructions(fallbackInstructions);
 * // Returns:
 * // [
 * //   { stepNumber: 1, instruction: "Grill a pork tenderloin fillet\nHeat oven to 400°F | 200°C. Grill a pork tenderloin fillet in the oven for about 40 minutes" }
 * // ]
 */
